import React, { useState } from 'react';
import axios from 'axios';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { CheckCircle2, Users, Shield, Calendar } from 'lucide-react';
import usePageMeta from '../hooks/usePageMeta';
import { trackEvent } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  firstName: '',
  location: '',
  email: '',
  experience: '',
  preferredMonth: '',
  friend: '',
  message: '',
  adult: '',
  gender: '',
};

const InfoCard = ({ icon: Icon, title, text }) => (
  <div className="bg-white border border-neutral-200 p-8 text-center">
    <Icon size={32} className="mx-auto text-neutral-900 mb-4" strokeWidth={1.5} />
    <h3 className="font-serif text-xl text-neutral-900 mb-2">{title}</h3>
    <p className="text-neutral-600 text-sm leading-relaxed">{text}</p>
  </div>
);

const GroupSpanking = () => {
  usePageMeta({
    title: 'Group Spanking Sessions — Seattle',
    description: 'Monthly group spanking sessions in the Seattle area. 3-6 female participants gather in a safe, professional environment. Fill out the request form to get on the list.',
    path: '/group-spanking',
  });

  const { toast } = useToast();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.adult !== 'yes') {
      toast({ title: 'Age verification required', description: 'You must confirm you are 18 or older.' });
      return;
    }
    if (!form.email || !form.message) {
      toast({ title: 'Missing information', description: 'Please provide your email and a short message.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/group-spanking-request`, {
        first_name: form.firstName || null,
        location: form.location || null,
        email: form.email.trim(),
        experience: form.experience || null,
        preferred_month: form.preferredMonth || null,
        friend: form.friend || null,
        message: form.message.trim(),
        adult: form.adult,
        gender: form.gender || null,
      });
      setSubmitted(true);
      trackEvent('group_spanking_request', { preferred_month: form.preferredMonth || 'none' });
      toast({ title: 'Request received', description: "You've been added to the list. I'll be in touch privately." });
    } catch (err) {
      toast({
        title: 'Submission failed',
        description: err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Together in trust"
        title="Group Spanking"
        subtitle="Monthly small-group sessions in a safe, controlled setting."
        image="https://customer-assets-wrfwihn1.emergentagent.net/job_realotk-rebuild/artifacts/p6arr3gm_watermarked_img_12740385330314874930.jpg"
      />

      {/* Intro copy */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 md:py-24 text-center">
          <p className="text-neutral-500 text-xs uppercase tracking-[0.35em] mb-6">What it is</p>
          <h2 className="font-serif text-3xl md:text-5xl text-neutral-900 font-bold tracking-tight leading-[1.15]">
            Small, safe, and shared
          </h2>
          <div className="mt-8 mx-auto w-16 h-px bg-neutral-900" />
          <p className="mt-10 text-lg text-neutral-700 leading-relaxed">
            Group spanking sessions are held generally <strong>once a month on a Saturday</strong>{' '}
            and usually have <strong>3&ndash;6 participants</strong>. They take place in a setting
            that allows you to watch each person receive their discipline properly until it is your
            turn. Because many women feel safer in numbers, it is a wonderful way to participate,
            observe, and relax knowing you are in a safe, controlled environment surrounded by
            others there for the same reason.
          </p>
        </div>
      </section>

      {/* Feature cards */}
      <section className="bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-6">
          <InfoCard
            icon={Calendar}
            title="Monthly on Saturday"
            text="Sessions are typically held one Saturday each month. Exact dates and location shared privately after your request is accepted."
          />
          <InfoCard
            icon={Users}
            title="3–6 Participants"
            text="Small, intimate groups. You'll watch each person receive their discipline properly until it's your turn."
          />
          <InfoCard
            icon={Shield}
            title="Safer Together"
            text="Many women feel more at ease in a group. Everyone here is a consenting adult in a controlled and confidential environment."
          />
        </div>
      </section>

      {/* Request form */}
      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="text-center mb-12">
            <p className="text-neutral-500 text-xs uppercase tracking-[0.35em] mb-4">Reserve your spot</p>
            <h2 className="font-serif text-3xl md:text-4xl text-neutral-900 font-bold">
              Group Spanking Request Form
            </h2>
            <p className="text-neutral-600 mt-4 max-w-xl mx-auto">
              Fill this out to get on the list. All requests are confidential and reviewed personally.
            </p>
          </div>

          {submitted ? (
            <div className="bg-white border border-neutral-200 p-10 md:p-14 text-center">
              <CheckCircle2 size={48} className="mx-auto text-neutral-900 mb-4" />
              <h3 className="font-serif text-3xl text-neutral-900 mb-3">You&rsquo;re on the list</h3>
              <p className="text-neutral-600 max-w-md mx-auto">
                Thank you for your request. You will receive a private reply with the next date,
                location details, and any questions I have for you.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 p-8 md:p-12 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName" className="text-xs uppercase tracking-widest text-neutral-700">First Name</Label>
                  <Input id="firstName" value={form.firstName} onChange={handleChange('firstName')} className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 h-11" />
                </div>
                <div>
                  <Label htmlFor="location" className="text-xs uppercase tracking-widest text-neutral-700">Location</Label>
                  <Input id="location" value={form.location} onChange={handleChange('location')} className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 h-11" />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-neutral-700">Your Email *</Label>
                <Input id="email" type="email" required value={form.email} onChange={handleChange('email')} className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 h-11" />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-700">Experience Level</Label>
                  <Select value={form.experience} onValueChange={(v) => setForm({ ...form, experience: v })}>
                    <SelectTrigger className="mt-2 rounded-none border-neutral-300 h-11">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="first-time">First time / never spanked</SelectItem>
                      <SelectItem value="some">A little experience</SelectItem>
                      <SelectItem value="experienced">Experienced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-700">Preferred Month</Label>
                  <Input value={form.preferredMonth} onChange={handleChange('preferredMonth')} placeholder="e.g. Next month, or any" className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 h-11" />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-700">Would you bring a friend?</Label>
                <RadioGroup value={form.friend} onValueChange={(v) => setForm({ ...form, friend: v })} className="flex gap-6 mt-3">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="friend-yes" />
                    <Label htmlFor="friend-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="friend-no" />
                    <Label htmlFor="friend-no" className="cursor-pointer">No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="maybe" id="friend-maybe" />
                    <Label htmlFor="friend-maybe" className="cursor-pointer">Maybe</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="message" className="text-xs uppercase tracking-widest text-neutral-700">Tell me about yourself and what you&rsquo;re looking for *</Label>
                <Textarea id="message" required rows={5} value={form.message} onChange={handleChange('message')} className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 resize-none" placeholder="Anything I should know before you join the group..." />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-700">Are you 18+?</Label>
                <RadioGroup value={form.adult} onValueChange={(v) => setForm({ ...form, adult: v })} className="flex gap-6 mt-3">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="grp-adult-yes" />
                    <Label htmlFor="grp-adult-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="grp-adult-no" />
                    <Label htmlFor="grp-adult-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-none bg-neutral-900 text-white hover:bg-neutral-700 h-12 text-sm tracking-widest uppercase disabled:opacity-60"
              >
                {submitting ? 'Sending...' : 'Add Me To The List'}
              </Button>
              <p className="text-xs text-neutral-500 text-center">
                Your information stays private and is never shared with other participants.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default GroupSpanking;
